import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { ArrowRight, ChevronDown, MoreHorizontal, Sparkles } from "lucide-react";
import {
  tableActionClass,
  tableActionCompactClass,
  tableActionInfoClass,
  tableActionMutedClass,
  tableActionPrimaryClass,
} from "./tableActionStyles";

const VARIANT_CLASSES = {
  default: tableActionClass,
  primary: tableActionPrimaryClass,
  info: tableActionInfoClass,
  muted: tableActionMutedClass,
  compact: tableActionCompactClass,
};

const ICON_TONES = {
  primary: "bg-primary/[0.08] text-primary ring-1 ring-primary/[0.08]",
  emerald: "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-600/10",
  sky: "bg-sky-50 text-sky-600 ring-1 ring-sky-600/10",
  amber: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/10",
  rose: "bg-rose-50 text-rose-600 ring-1 ring-rose-600/10",
};

function getVariantClass(variant = "default") {
  return VARIANT_CLASSES[variant] ?? tableActionClass;
}

function getIconTone(tone = "primary") {
  return ICON_TONES[tone] ?? ICON_TONES.primary;
}

function stopPropagation(event) {
  event.stopPropagation();
}

function ActionLink({ action, className, showArrow = false, onNavigate, children }) {
  const Icon = action.icon;
  const content =
    children ??
    (showArrow ? (
      <>
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
        <span className="truncate">{action.label}</span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
      </>
    ) : (
      children
    ));

  const handleClick = (event) => {
    stopPropagation(event);
    action.onClick?.(event);
    onNavigate?.();
  };

  if (action.to) {
    return (
      <Link to={action.to} className={className} onClick={handleClick} role="menuitem">
        {content}
      </Link>
    );
  }

  if (action.href) {
    return (
      <a href={action.href} className={className} onClick={handleClick} role="menuitem">
        {content}
      </a>
    );
  }

  return (
    <button type="button" className={className} onClick={handleClick} role="menuitem">
      {content}
    </button>
  );
}

function MenuItem({ action, onNavigate }) {
  const Icon = action.icon;

  return (
    <ActionLink
      action={action}
      onNavigate={onNavigate}
      className="group/item flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-all duration-150 hover:bg-gradient-to-r hover:from-primary/[0.04] hover:to-accent/[0.06] focus-visible:bg-primary/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/25"
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-transform duration-150 group-hover/item:scale-105 ${getIconTone(action.tone)}`}
      >
        {Icon && <Icon className="h-4 w-4" strokeWidth={2.25} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold leading-tight text-primary">
          {action.label}
        </span>
        {action.description && (
          <span className="mt-0.5 block truncate text-[11px] leading-snug text-primary/45">
            {action.description}
          </span>
        )}
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 translate-x-0 text-primary/20 opacity-0 transition-all duration-150 group-hover/item:translate-x-0.5 group-hover/item:text-primary/50 group-hover/item:opacity-100" />
    </ActionLink>
  );
}

export default function TableActionsMenu({ actions = [] }) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState("bottom");
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight ?? actions.length * 56 + 56;
    const menuWidth = menu?.offsetWidth ?? 272;
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    setPlacement(openUp ? "top" : "bottom");
    setMenuStyle({
      top: openUp ? rect.top - menuHeight - 8 : rect.bottom + 8,
      left: Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12)),
    });
  }, [actions.length]);

  useLayoutEffect(() => {
    if (!open) {
      setVisible(false);
      return undefined;
    }

    updatePosition();
    const frame = requestAnimationFrame(() => {
      updatePosition();
      setVisible(true);
    });

    return () => cancelAnimationFrame(frame);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (actions.length === 0) return null;

  if (actions.length === 1) {
    const action = actions[0];
    return (
      <ActionLink action={action} className={getVariantClass(action.variant)} showArrow />
    );
  }

  return (
    <>
      <div className="relative inline-flex" ref={triggerRef}>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={`${actions.length} actions available`}
          onClick={(event) => {
            stopPropagation(event);
            setOpen((value) => !value);
          }}
          className={`inline-flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-xs font-semibold shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:ring-offset-1 ${
            open
              ? "border-primary/20 bg-gradient-to-r from-primary/[0.08] to-accent/[0.12] text-primary shadow-md shadow-primary/10"
              : "border-primary/10 bg-white text-primary/70 hover:border-primary/15 hover:bg-primary/[0.03] hover:text-primary"
          }`}
        >
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-lg transition-colors ${
              open ? "bg-primary text-white" : "bg-primary/[0.08] text-primary"
            }`}
          >
            <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={2.5} />
          </span>
          <span className="hidden sm:inline">Actions</span>
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-accent/25 px-1.5 text-[10px] font-bold tabular-nums text-primary">
            {actions.length}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 text-primary/40 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            strokeWidth={2.5}
          />
        </button>
      </div>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Row actions"
            style={{ top: menuStyle.top, left: menuStyle.left }}
            className={`fixed z-[9999] w-[min(17rem,calc(100vw-1.5rem))] origin-top overflow-hidden rounded-2xl border border-primary/10 bg-white shadow-card ring-1 ring-primary/[0.05] transition-all duration-200 ease-out ${
              visible
                ? "translate-y-0 scale-100 opacity-100"
                : placement === "top"
                  ? "translate-y-1 scale-[0.98] opacity-0"
                  : "-translate-y-1 scale-[0.98] opacity-0"
            }`}
            onClick={stopPropagation}
          >
            <div className="h-1 bg-gradient-to-r from-primary via-primary-light to-accent" aria-hidden />
            <div className="flex items-center gap-2 border-b border-primary/[0.06] bg-gradient-to-r from-primary/[0.02] to-accent/[0.05] px-3 py-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-primary shadow-sm ring-1 ring-primary/[0.06]">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2.25} />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/45">
                  Quick actions
                </p>
                <p className="truncate text-xs font-semibold text-primary/70">
                  {actions.length} options available
                </p>
              </div>
            </div>
            <div className="space-y-0.5 p-1.5">
              {actions.map((action) => (
                <MenuItem
                  key={action.key ?? action.label}
                  action={action}
                  onNavigate={() => setOpen(false)}
                />
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
